import { memo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { VehicleSocket } from "../../../../store/admin/vehiclesSocketStore";
import { useVehiclesSocketStore } from "../../../../store/admin/vehiclesSocketStore";
import IgnitionIcon from "../../icons/biggerIcons/IgnitionIcon";
import BatteryIcon from "../../icons/biggerIcons/BatteryIcon";
import SignIcon from "../../icons/biggerIcons/SignIcon";
import SosIcon from "../../icons/biggerIcons/SosIcon";
import { getActiveTripForUnit, useActiveTripsStore } from "../../../../store/admin/activeTripsStore";
import { liveSpeedKmh, readEngine } from "./engineDisplay";
import UnitBell from "./UnitBell";
import { rowSpeed, shortAgo } from "./compactRow";
import { TONE_COLOR, batteryTone, connectionTone, gpsTone, odometerTone } from "./indicators";
import OdometerIcon from "../../../../pages/home/components/icons/Odometer";
import Tooltip from "../../../../components/ui/Tooltip";
import { getTimeAgo } from "../../../../utils/getDate";
import { HITCH_MODELS, matchesModel } from "../../../../utils/vehicleParamsResolver";
import { UNIT_STATUS_COLORS as STATUS_COLORS, ENGINE_COLORS } from "../../../../constants/unitColors";

const PANIC_CODES = new Set<number>([5]);

// Tooltip de los indicadores: el componente del proyecto (el mismo de la
// tarjeta), no `title`, que tarda ~1 s y no se ve en táctil. 150 ms de espera
// para que no parpadeen al pasar el cursor de corrido por la lista.
// Tooltip envuelve en un div `inline-block w-full`: sin `!w-auto` cada ícono
// se estira y las columnas de semáforos dejan de alinearse entre filas.
const Tip = ({ content, children }: { content: string; children: React.ReactNode }) => (
  <Tooltip content={content} delay={150} position="top" className="!flex !w-auto shrink-0 items-center">
    {children}
  </Tooltip>
);

interface VehicleRowProps {
  vehicle: VehicleSocket;
  isExpanded: boolean;
  onToggle: (vehicleId: string) => void;
  isDarkMode: boolean;
  themeClasses: {
    text: string;
    textSecondary: string;
    buttonHover: string;
  };
}

// Una unidad en una línea (~40 px), al estilo de la lista de Wialon. Dos
// grupos a la derecha:
// - Avisos que sólo aparecen cuando aplican: SOS, paro de motor activo,
//   viaje en curso. Si están siempre, dejan de verse.
// - Semáforos en columnas FIJAS (ignición, batería, GPS): siempre en la misma
//   posición, para que el ojo baje por la columna y vea el color raro sin leer,
//   que es lo que hace útil la lista de Wialon.
// El "hace cuánto" se colorea con la frescura del reporte. El detalle
// completo sigue en VehicleCard, que se despliega debajo al tocar la fila.
function VehicleRowComponent({
  vehicle,
  isExpanded,
  onToggle,
  isDarkMode,
  themeClasses,
}: VehicleRowProps) {
  const { t } = useTranslation("lite");
  // Datos frescos del socket (O(1) vía Map), igual que VehicleCard.
  const fresh = useVehiclesSocketStore((state) => {
    const idx = state.vehiclesMap.get(vehicle.id);
    return idx !== undefined ? state.vehicles[idx] : undefined;
  });
  // La unidad seguida en el mapa (se eligió ahí o aquí): la fila lo muestra y
  // se desplaza a la vista. Antes sólo se marcaba la desplegada aquí, así que
  // al tocar la unidad en el mapa la lista no daba señal de cuál era.
  const selected = useVehiclesSocketStore((s) => s.focusedVehicleId === vehicle.id);
  const rowRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (selected) rowRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selected]);
  // timeTick avanza cada 30 s y re-renderiza el "hace cuánto".
  useVehiclesSocketStore((state) => state.timeTick);
  const v = fresh || vehicle;

  const name = v.vehicle?.name || v.imei || v.id;
  const plates = v.vehicle?.plates;
  const driver = v.driver?.name && v.driver.name !== "Sin asignar" ? v.driver.name : "";
  const engine = readEngine(v.speed, v.status, v.engineStatus);
  const speed = rowSpeed(liveSpeedKmh(v.speed, v.status));
  const ago = shortAgo(v.lastReport._seconds, Date.now(), t("compactList.now"));
  const panic = v.params?.panic != null && PANIC_CODES.has(Number(v.params.panic));
  const statusColor = STATUS_COLORS[v.status] || STATUS_COLORS.offline;
  const now = Date.now();
  const battery = batteryTone(v.battery);
  const gps = gpsTone(v.gps_sat);
  const freshness = connectionTone(v.lastReport._seconds, now);
  const odometer = odometerTone(v.odometer);
  const odometerText =
    v.odometer != null ? `${Math.round(v.odometer).toLocaleString()} km` : t("vehicleCard.noData");
  const isHitchModel = matchesModel(v.brandModel, HITCH_MODELS);
  const hitchLabel =
    v.hitch == null ? t("vehicleCard.noData") : v.hitch ? t("vehicleCard.hitched") : t("vehicleCard.unhitched");
  const engineLabel =
    engine.tone === "on"
      ? t("vehicleCard.statusOn")
      : engine.tone === "off"
        ? t("vehicleCard.statusOff")
        : t("vehicleCard.noData");
  const lastConnectionText = getTimeAgo(v.lastReport._seconds, {
    noData: t("time.noData"),
    rightNow: t("time.rightNow"),
    min: t("time.min"),
    hour: t("time.hour"),
    hours: t("time.hours"),
    day: t("time.day"),
    days: t("time.days"),
    month: t("time.month"),
    months: t("time.months"),
    and: t("time.and"),
  });
  const inTrip = useActiveTripsStore(
    (state) => !!getActiveTripForUnit(v.vehicle_id, v.remolque_id, v.id, state),
  );

  return (
    <button
      ref={rowRef}
      type="button"
      onClick={() => onToggle(v.id)}
      aria-expanded={isExpanded}
      title={[name, plates && plates !== name ? plates : "", driver].filter(Boolean).join(" · ")}
      className={`w-full h-10 px-2.5 flex items-center gap-2 rounded-md text-left transition-colors active:translate-y-px ${
        panic
          ? "bg-red-500/10 ring-1 ring-inset ring-red-500/50"
          : selected
            ? `ring-1 ring-inset ring-primary-500/70 ${isDarkMode ? "bg-dark-card" : "bg-gray-100"}`
            : isExpanded
              ? isDarkMode ? "bg-dark-card" : "bg-gray-100"
              : themeClasses.buttonHover
      }`}
    >
      <span
        className={`w-2 h-2 rounded-full shrink-0 ${v.status === "driving" ? "animate-pulse" : ""}`}
        style={{ backgroundColor: statusColor }}
      />
      {/* Nombre y conductor comparten el espacio flexible y son lo único que se
          trunca: así los semáforos de la derecha caen en la misma posición en
          todas las filas, también a 390 px con nombres largos
          ("Eco 205 36-AF-8M" desbordaba la fila). */}
      <span className="flex-1 min-w-0 flex items-baseline gap-2">
        <span className={`font-mono font-semibold text-[13px] max-w-full truncate shrink-0 ${themeClasses.text}`}>
          {name}
        </span>
        <span className={`min-w-0 flex-1 truncate text-xs ${themeClasses.textSecondary}`}>{driver}</span>
      </span>
      {/* Avisos: sólo cuando aplican (si estuvieran siempre, dejarían de verse). */}
      {isHitchModel && (
        <Tip content={`${t("vehicleCard.hitch")}: ${hitchLabel}`}>
          <i
            className={`${v.hitch ? "ri-link" : "ri-link-unlink"} text-sm leading-none`}
            style={{ color: v.hitch ? TONE_COLOR.green : TONE_COLOR.gray }}
          />
        </Tip>
      )}
      {v.engineCutoff && (
        <Tip content={`${t("vehicleCard.engineCutoff")}: ${t("vehicleCard.engineCutoffActive")}`}>
          <i className="ri-lock-2-fill text-sm leading-none" style={{ color: TONE_COLOR.red }} />
        </Tip>
      )}
      {inTrip && (
        <Tip content={t("vehicleCard.activeTrip", "Viaje en curso")}>
          <i className="ri-route-line text-sm leading-none text-primary-500" />
        </Tip>
      )}
      {/* Semáforos en columnas fijas, juntos y apretados (gap-1.5): misma
          posición en todas las filas para leerlos como columna. */}
      <span className="flex items-center gap-1.5 shrink-0">
        {/* SOS primero: es el que exige acción. Con pánico, pastilla roja con
            halo que late (animate-sosRing); con "reducir movimiento" queda
            fija, la alerta no depende del movimiento para leerse. */}
        <Tip
          content={`${t("vehicleCard.panic")}: ${
            panic ? t("vehicleCard.engineCutoffActive") : t("vehicleCard.engineCutoffInactive")
          }`}
        >
          <span className="relative w-7 h-4 flex items-center justify-center">
            {panic && (
              <span aria-hidden className="absolute inset-0 rounded bg-red-500 motion-safe:animate-sosRing" />
            )}
            <span className={`relative w-7 h-4 flex items-center justify-center rounded ${panic ? "bg-red-500" : ""}`}>
              <SosIcon color={panic ? "#FFFFFF" : TONE_COLOR.gray} className="w-[22px] h-auto" />
            </span>
          </span>
        </Tip>
        {/* La campanita va pegada al SOS: los dos son avisos de la unidad. */}
        <UnitBell deviceId={v.id} vehicleId={v.vehicle_id} unitName={name} isDarkMode={isDarkMode} variant="row" />
        <Tip content={`${t("vehicleCard.ignition")}: ${engineLabel}`}>
          <IgnitionIcon color={ENGINE_COLORS[engine.tone]} className="w-4 h-4" />
        </Tip>
        <Tip
          content={`${t("vehicleCard.battery")}: ${
            v.battery != null ? `${Math.round(v.battery)}%` : t("vehicleCard.noData")
          }`}
        >
          <BatteryIcon color={TONE_COLOR[battery]} className="w-4 h-4" />
        </Tip>
        <Tip content={`${t("vehicleCard.gpsSignal")}: ${v.gps_sat ?? t("vehicleCard.noData")}`}>
          <SignIcon color={TONE_COLOR[gps]} className="w-4 h-4" />
        </Tip>
        <Tip content={`${t("vehicleCard.odometer")}: ${odometerText}`}>
          <OdometerIcon color={TONE_COLOR[odometer]} className="w-4 h-4" />
        </Tip>
      </span>
      <span className={`shrink-0 w-12 sm:w-14 text-right text-xs tabular-nums ${themeClasses.text}`}>
        {speed}
      </span>
      <Tip content={`${t("vehicleCard.lastConnection")}: ${lastConnectionText}`}>
        <span
          className={`block w-11 text-right text-xs tabular-nums ${freshness === "gray" ? themeClasses.textSecondary : ""}`}
          style={freshness === "gray" ? undefined : { color: TONE_COLOR[freshness] }}
        >
          {ago}
        </span>
      </Tip>
      <i
        className={`ri-arrow-down-s-line shrink-0 text-base transition-transform duration-200 ${themeClasses.textSecondary} ${
          isExpanded ? "rotate-180" : ""
        }`}
      />
    </button>
  );
}

// themeClasses llega como objeto nuevo en cada render del padre: con la
// comparación por defecto las 31 filas se volvían a dibujar (y a traducir
// ~15 textos cada una) en cada reporte del socket. Cada fila ya se suscribe a
// su propia unidad en el store; basta comparar lo que cambia su aspecto.
export default memo(
  VehicleRowComponent,
  (a, b) =>
    a.vehicle === b.vehicle &&
    a.isExpanded === b.isExpanded &&
    a.isDarkMode === b.isDarkMode &&
    a.onToggle === b.onToggle,
);
